// SPDX-License-Identifier: MIT
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiErrorMessage, quizApi, type QuizResult } from '../../api/client'
import { CheckCircle, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/authStore'

interface Props { slug: string; lessonId: string; onComplete?: () => void }

export default function QuizPanel({ slug, lessonId, onComplete }: Props) {
  const [answers, setAnswers] = useState<Record<string, string[]>>({})
  const [result, setResult] = useState<QuizResult | null>(null)
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const queryKey = ['quiz', slug, lessonId, user?.id]
  const { data: quiz, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => quizApi.get(slug, lessonId).then(r => r.data),
  })
  const submitMut = useMutation({
    mutationFn: () => {
      if (!quiz) throw new Error('Load the knowledge check before submitting')
      return quizApi.submit(slug, lessonId, answers, quiz.content_revision)
    },
    onSuccess: ({ data }) => {
      setResult(data)
      if (quiz) qc.setQueryData(queryKey, {
        ...quiz, attempts_used: data.attempts_used, has_passed: quiz.has_passed || data.passed,
      })
      if (data.passed) {
        toast.success(`Passed! Score ${data.score}%`, { id: 'knowledge-check' })
        qc.invalidateQueries({ queryKey: ['course', slug] })
        qc.invalidateQueries({ queryKey: ['lesson', slug, lessonId] })
        qc.invalidateQueries({ queryKey: ['course-progress', slug] })
        qc.invalidateQueries({ queryKey: ['my-certs'] })
        onComplete?.()
      } else toast.error(`Score ${data.score}%. Required score ${data.pass_score}%.`, { id: 'knowledge-check' })
    },
    onError: (failure: unknown) => {
      toast.error(apiErrorMessage(failure, 'Unable to submit this knowledge check'))
      qc.invalidateQueries({ queryKey })
    },
  })
  if (isLoading) return <p className="text-brand-muted py-4">Loading knowledge check...</p>
  if (error) return <div className="card" role="alert">{apiErrorMessage(error, 'Unable to load the knowledge check')} <button className="btn-outline" onClick={() => refetch()}>Retry</button></div>
  if (!quiz || !quiz.questions.length) return <p role="alert">This knowledge check has no configured questions. Contact the course instructor.</p>
  const remaining = quiz.max_attempts - quiz.attempts_used
  const answered = quiz.questions.every(question => (answers[question.id] || []).some(value => value.trim().length > 0))
  const toggle = (id: string, choice: string, multi: boolean) => {
    setAnswers(previous => {
      const current = previous[id] || []
      return {
        ...previous,
        [id]: multi ? current.includes(choice) ? current.filter(value => value !== choice) : [...current, choice] : [choice],
      }
    })
  }
  return (
    <div className="border border-brand-border rounded-xl p-6 mb-6 bg-brand-surface">
      <h2 className="font-bold text-lg mb-1">{quiz.title}</h2>
      <p className="text-brand-muted text-sm mb-6">Required score {quiz.pass_score}%. Attempts remaining {Math.max(0, remaining)} of {quiz.max_attempts}.</p>
      {quiz.has_passed && !result && <p className="mb-4 text-brand-green" role="status">You have already passed this knowledge check.</p>}
      {quiz.questions.map((question, index) => (
        <fieldset key={question.id} className="mb-6" disabled={!!result || submitMut.isPending || remaining <= 0}>
          <legend className="font-semibold text-sm mb-3">{index + 1}. {question.body}</legend>
          {question.question_type === 'short_answer' ? (
            <input type="text" aria-label={`Answer to question ${index + 1}`} className="input w-full"
              value={answers[question.id]?.[0] || ''}
              onChange={event => setAnswers(previous => ({ ...previous, [question.id]: [event.target.value] }))} />
          ) : (
            <div className="space-y-2">
              {question.choices?.map(choice => (
                <label key={choice.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer ${answers[question.id]?.includes(choice.id) ? 'border-brand-blue bg-blue-50' : 'border-brand-border'}`}>
                  <input type={question.question_type === 'multi_select' ? 'checkbox' : 'radio'} name={question.id}
                    checked={answers[question.id]?.includes(choice.id) || false}
                    onChange={() => toggle(question.id, choice.id, question.question_type === 'multi_select')}
                    className="accent-brand-blue" />
                  <span className="text-sm">{choice.text}</span>
                </label>
              ))}
            </div>
          )}
          {result?.feedback[question.id] && (
            <p className={`mt-2 flex items-start gap-2 text-sm ${result.feedback[question.id].correct ? 'text-brand-green' : 'text-red-700'}`}>
              {result.feedback[question.id].correct ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {result.feedback[question.id].explanation || (result.feedback[question.id].correct ? 'Correct' : 'Incorrect')}
            </p>
          )}
        </fieldset>
      ))}
      {submitMut.isError && <p className="text-red-700 mb-3" role="alert">{apiErrorMessage(submitMut.error, 'Unable to submit the knowledge check')}</p>}
      {!result ? (
        <button className="btn-primary" onClick={() => submitMut.mutate()} disabled={!answered || submitMut.isPending || remaining <= 0}>
          {submitMut.isPending ? 'Grading...' : 'Submit Knowledge Check'}
        </button>
      ) : (
        <div className={`p-4 rounded-lg border ${result.passed ? 'border-brand-green bg-green-50' : 'border-red-300 bg-red-50'}`} role="status">
          <p className="font-bold">{result.passed ? 'Passed' : 'Not passed'}</p>
          <p className="text-sm">{result.score}%. {result.correct_count} of {result.total_questions} correct.</p>
          {!result.passed && remaining > 0 && <button className="btn-outline mt-3" onClick={() => {
            setAnswers({})
            setResult(null)
            submitMut.reset()
          }}>Review and Retry</button>}
        </div>
      )}
      {remaining <= 0 && !quiz.has_passed && <p className="mt-3" role="alert">The attempt limit has been reached. Contact the instructor if you need help.</p>}
    </div>
  )
}
